import { Component, DestroyRef, OnInit, inject, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs/operators";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { GetUserService } from "../../../data/user/get-user.service";
import { UpdateUserService } from "../../../data/user/update-user.service";

import dayjs from "dayjs";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { USER_ACTIVATION_I18N } from "./user-activation.i18n";

@Component({
  selector: "app-user-activation",
  templateUrl: "./user-activation.component.html",
  styleUrls: ["./user-activation.component.scss"],
  imports: SHARED_IMPORTS,
})
export class UserActivationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = USER_ACTIVATION_I18N;

  // Loading state for skeleton - true initially to show skeleton until data is ready
  isLoading = signal(true);
  isSending = signal(false);

  public validId: boolean = false;
  public accountData: AccountData;
  private defaultAccountData: AccountData = {
    user: {
      userId: "",
      code: "",
      lastName: "",
      firstName: "",
    },
    contractInfo: {
      contractStatus: "",
      contractStartAt: "--/--/--",
      contractEndAt: "--/--/--",
    },
    commonInfo: {
      lastModifiedAt: "--/--/--",
      lastModifiedBy: "--",
    },
    id: "",
  };

  accountStatus = [
    { value: "OPEN", viewValue: this.i18n.status.active },
    { value: "CLOSE", viewValue: this.i18n.status.inactive },
  ];
  selectedStatus = "";

  userId: string;
  userName: string;
  sectionId: string;
  putParam;

  constructor(
    public dialogRef: MatDialogRef<UserActivationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _matdialog: MatDialog,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar,
    private getUserService: GetUserService,
    private updateUserService: UpdateUserService,
    private common: DatabaseCacheService,
  ) {
    this.accountData = this.defaultAccountData;
  }

  ngOnInit(): void {
    this.resetFields();
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.userId = this.data?.id;
      this.sectionId = this.common.getSelectedSection().sectionId;
      this.getUser();
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  resetFields(): void {
    this.validId = false;
    this.selectedStatus = "";
    this.accountData = this.defaultAccountData;
  }

  getUser() {
    this.isLoading.set(true);
    this.resetFields();

    this.getUserService.getUser(this.userId, false)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (res) => {
          let convertedRes = res as Array<any>;

          if (convertedRes.length == 0) {
            this.snackBar.open("ERROR: invalid ID", null, {
              duration: 5000,
            });
          } else {
            let userId = convertedRes[0].user.userId;

            if (userId === "ANONYMOUS") {
              this.snackBar.open("ERROR: invalid ID", null, {
                duration: 5000,
              });
            } else {
              this.accountData = {
                user: {
                  userId: userId,
                  code: convertedRes[0].user.code ?? "--",
                  lastName: convertedRes[0].user.lastName ?? "--",
                  firstName: convertedRes[0].user.firstName ?? "--",
                },
                contractInfo: {
                  contractStatus:
                    convertedRes[0].contractInfo.contractStatus.toUpperCase(),
                  contractStartAt: dayjs(
                    convertedRes[0].contractInfo.contractStartAt,
                  ).isValid()
                    ? dayjs(convertedRes[0].contractInfo.contractStartAt).format(
                        "YYYY/MM/DD",
                      )
                    : "--/--/--",
                  contractEndAt: dayjs(
                    convertedRes[0].contractInfo.contractEndAt,
                  ).isValid()
                    ? dayjs(convertedRes[0].contractInfo.contractEndAt).format(
                        "YYYY/MM/DD",
                      )
                    : "--/--/--",
                },
                commonInfo: {
                  lastModifiedAt: convertedRes[0].commonInfo.lastModifiedAt,
                  lastModifiedBy: convertedRes[0].commonInfo.lastModifiedBy,
                },
                id: convertedRes[0].id,
              };

              this.selectedStatus =
                convertedRes[0].contractInfo.contractStatus.toUpperCase();
              // this.selectedGroup = convertedRes[0].permissions.userType.toUpperCase();
              this.validId = true;
            }
          }
        },
        error: () => {
          // finalize handles isLoading.set(false)
        },
      });
  }

  deactivate() {
    this.selectedStatus = "CLOSE";

    this.updateUser();
  }

  reactivate() {
    this.selectedStatus = "OPEN";
    this.updateUser();
  }

  updateUser() {
    this.putParam = {
      userType: "USER",
      contractStatus: this.selectedStatus,
    };
    this.isSending.set(true);
    this.updateUserService.updateUser(this.userId, this.putParam)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe(
        (res) => {
          this.common.parametersUpdated.next({ type: 'usersListUpdated', sectionId: this.sectionId ?? null });
          this.snackBar.open(this.i18n.message.updateSuccess, this.i18n.button.close, { duration: 3000 });
          this.dialogRef.close({ needRefresh: true });
        },
        (error) => {
          this.snackBar.open(this.i18n.message.updateError, this.i18n.button.close, { duration: 5000 });
        },
      );
  }

  update() {
    this.updateUser();
  }

  ConcatenateUserName(user: AccountData["user"]): string {
    this.userName = "";
    let _user = user;

    if (_user.userId === "ANONYMOUS") {
      this.userName = this.i18n.userName.anonymous;
    } else if (_user.code === "--") {
      this.userName = _user.lastName + " " + _user.firstName;
    } else {
      this.userName = _user.code;
    }
    return this.userName;
  }
}

export interface AccountData {
  user: {
    userId: string;
    code: string;
    lastName: string;
    firstName: string;
  };
  contractInfo: {
    contractStatus: string;
    contractStartAt: string;
    contractEndAt: string;
  };
  commonInfo: {
    lastModifiedAt: string;
    lastModifiedBy: string;
  };
  id: string;
}
